import {
	forwardRef, useEffect, useImperativeHandle, useMemo, useState,
} from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
	DATE_TIME_DISPLAY_FORMAT, DATE_TIME_ISO_FORMAT,
	DateTime,
	DatePicker,
	useDidUpdateEffect,
	DEFAULT_DROPDOWN_CLASSNAMES,
	cn,
	DatePickerValue,
	getDateRangeString,
} from '@aleph/shared-components';
import { ICellEditorParams, ICellEditor } from 'ag-grid-community';
import {
	flip,
	FloatingPortal, offset, size, useFloating,
} from '@floating-ui/react';

const DateTimeCellEditor = forwardRef<ICellEditor, ICellEditorParams & {
	minDate?: Dayjs,
	maxDate?: Dayjs,
	format?: string,
	initialValue?: Dayjs,
}>((
	params,
	ref,
) => {
	const [value, setValue] = useState<DatePickerValue | null>({
		from: dayjs(params.value ?? params.initialValue ?? undefined),
		to:   dayjs(params.value ?? params.initialValue ?? undefined),
	});

	const [finalValue, setFinalValue] = useState<DatePickerValue | null>(null);
	const [validationError, setValidationError] = useState<string | null>(null);
	const [, setInvalidValue] = useState<DatePickerValue | null>(null);

	// Normalize minDate/maxDate to start of day for date selection, but keep original for validation
	const normalizedMinDate = useMemo(() => {
		if (!params.minDate) return undefined;

		return params.minDate.startOf('day');
	}, [params.minDate]);

	const normalizedMaxDate = useMemo(() => {
		if (!params.maxDate) return undefined;

		return params.maxDate.startOf('day');
	}, [params.maxDate]);

	const { refs, floatingStyles } = useFloating({
		placement:  'bottom-start',
		middleware: [
			offset(2),
			flip(),
			size({
				apply({ availableHeight, elements }) {
					elements.floating.style.maxHeight = `${availableHeight}px`;
				},
			}),
		],
	});

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' || e.key === 'Tab' || e.key === 'Delete') {
				setValidationError(null);
				setInvalidValue(null);
				params.stopEditing();
				setValue(null);
			}
		};
		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, []);

	useImperativeHandle(ref, () => ({
		getValue() {
			// If there's a validation error, return the original value instead of invalid value
			if (validationError) {
				return params.value ? dayjs(params.value).format(DATE_TIME_ISO_FORMAT) : null;
			}

			return dayjs(value?.from).format(DATE_TIME_ISO_FORMAT);
		},
	}));

	function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
		if (e.key === 'Enter') {
			// Only finalize if there's no validation error
			if (!validationError) {
				setFinalValue(value);
			}
		}
	}

	function onDateChange(newDate: DatePickerValue) {
		if (getDateRangeString(newDate, true) === getDateRangeString({
			from: dayjs(params.value ?? undefined),
			to:   dayjs(params.value ?? undefined),
		}, true)) {
			return;
		}

		// Clear previous validation error
		setValidationError(null);
		setInvalidValue(null);

		// Validate that the selected datetime is after minDate (including time)
		if (newDate?.from && params.minDate) {
			if (newDate.from.isBefore(params.minDate)) {
				// Special case: if user selects the same date, hour, and minute as group start time
				// (which is minDate - 1 second), automatically add 1 second to make it valid
				const groupStartTime = params.minDate.subtract(1, 'second');
				const selectedDate = newDate.from.startOf('minute');
				const groupStartDate = groupStartTime.startOf('minute');

				if (selectedDate.isSame(groupStartDate, 'minute') && selectedDate.isSame(groupStartDate, 'day')) {
					// User selected the same time as group start, automatically adjust to minDate
					const adjustedDate = {
						...newDate,
						from: params.minDate,
						to:   params.minDate,
					};
					setValue(adjustedDate);

					if (adjustedDate?.changeSource === 'click') {
						setFinalValue(adjustedDate);
					}

					return;
				}

				const minDateFormatted = params.minDate.format(DATE_TIME_DISPLAY_FORMAT);
				setValidationError(`Date must be after ${minDateFormatted}`);
				setInvalidValue(newDate);
				// Still update value to show what user selected, but mark as invalid
				setValue(newDate);

				return;
			}
		}

		// Validate that the selected datetime is before maxDate (including time)
		if (newDate?.from && params.maxDate) {
			if (newDate.from.isAfter(params.maxDate)) {
				const maxDateFormatted = params.maxDate.format(DATE_TIME_DISPLAY_FORMAT);
				setValidationError(`Date must be before ${maxDateFormatted}`);
				setInvalidValue(newDate);
				// Still update value to show what user selected, but mark as invalid
				setValue(newDate);

				return;
			}
		}

		setValue(newDate);

		if (newDate?.changeSource === 'click') {
			setFinalValue(newDate);
		}
	}

	useDidUpdateEffect(
		() => {
			if (finalValue && !validationError) {
				params.stopEditing();
			}
		},
		[finalValue, validationError],
	);

	return (
		<div
			className="h-full flex items-center w-full"
			ref={refs.setReference}
		>
			<div className="px-[8px] w-full flex items-center justify-between">
				{
					params.value
						? dayjs(params.value).format(DATE_TIME_DISPLAY_FORMAT)
						: 'Never'
				}
				<DateTime />
			</div>
			<FloatingPortal>
				<div ref={refs.setFloating}
					style={floatingStyles}
					className={cn(DEFAULT_DROPDOWN_CLASSNAMES, 'p-3')}
					onScroll={(e) => e.stopPropagation()}
					onWheel={(e) => e.stopPropagation()}
					onKeyDown={onKeyDown}
				>
					{validationError && (
						<div className="mt-2 px-2 py-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded mb-[10px]">
							{validationError}
						</div>
					)}
					<DatePicker
						dateTime
						onChange={onDateChange}
						value={value}
						minDate={normalizedMinDate}
						maxDate={normalizedMaxDate}
					/>
				</div>
			</FloatingPortal>
		</div>
	);
});

export default DateTimeCellEditor;
